import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { SampleBadge, RealBadge } from "@/components/site/SampleBadge";
import { CtaBanner } from "@/components/site/CtaBanner";
import { GALLERY, GALLERY_GROUPS, GALLERY_TABS } from "@/lib/site";

const COLS = { 4: "lg:grid-cols-4", 5: "lg:grid-cols-5" };

const Group = ({ group }) => {
  const items = GALLERY.filter((g) => g.category === group.id);
  const cols = items.reduce((n, g) => n + (g.type === "ba" ? 2 : 1), 0);
  const real = items.some((g) => g.real);
  return (
    <motion.div layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }} data-testid={`gallery-group-${group.id}`}>
      <div className="mb-6 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[var(--navy)]">{group.label}</h2>
        <span className={`text-sm font-medium ${real ? "text-[var(--green)]" : "text-slate-500"}`}>{real ? "Real project photos" : "Sample images – real photos coming soon"}</span>
      </div>
      <div className={`grid gap-x-5 gap-y-10 md:grid-cols-2 ${COLS[cols % 5 === 0 ? 5 : 4]}`}>
        {items.map((g) => <Tile key={g.id} g={g} />)}
      </div>
    </motion.div>
  );
};

const Tile = ({ g }) => (
  <figure className={g.type === "ba" ? "md:col-span-2" : ""} data-testid={`gallery-item-${g.id}`}>
    {g.type === "ba" ? (
      <BeforeAfter testId={`gallery-ba-${g.id}`} real={g.real} before={g.before} after={g.after} beforeAlt={g.beforeAlt} afterAlt={g.afterAlt} className="aspect-[4/3] md:aspect-[16/10]" />
    ) : (
      <div className="group relative aspect-[4/3] md:aspect-[10/13] overflow-hidden rounded-2xl bg-slate-200">
        <img src={g.src} alt={g.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        {g.real ? <RealBadge /> : <SampleBadge />}
      </div>
    )}
    <figcaption className="mt-3 font-display font-semibold text-[var(--navy)]">{g.title}</figcaption>
  </figure>
);

export default function Gallery() {
  const [tab, setTab] = useState("all");
  const groups = GALLERY_GROUPS.filter((g) => tab === "all" || g.id === tab);
  return (
    <>
      <Seo title="Project Gallery | Easy Handyman Services Oxfordshire"
        description="Interior improvements, garden maintenance and house cleaning projects across Oxfordshire. Before-and-after layouts – real project photos coming soon." />
      <PageHero testId="gallery-hero" eyebrow="Project gallery" lines={["Before, after", "and everything between."]}
        intro="Photos marked ‘Our project’ are from our own work. Garden and cleaning photos are clearly labelled stock samples until our real project photos are added." />
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="mb-10 flex gap-2 overflow-x-auto pb-2 -mx-6 px-6 sm:mx-0 sm:px-0" >
            {GALLERY_TABS.map((t) => (
              <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`gallery-filter-${t.id}`} aria-pressed={tab === t.id}
                className={`relative shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${tab === t.id ? "text-white" : "text-[var(--navy)] bg-[var(--grey)] hover:bg-slate-200"}`}>
                {tab === t.id && <motion.span layoutId="gallery-pill" className="absolute inset-0 rounded-full bg-[var(--navy)]" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
                <span className="relative">{t.label}</span>
              </button>
            ))}
          </Reveal>
          <div className="space-y-20" data-testid="gallery-grid">
            <AnimatePresence initial={false}>{groups.map((g) => <Group key={g.id} group={g} />)}</AnimatePresence>
          </div>
        </div>
      </section>
      <CtaBanner />
    </>
  );
}
